/**
 * Дашборд плановых заказов: форма ответа ручки, подписи ступеней и адреса
 * переходов на «Планы».
 *
 * Спека: `docs/specs/2026-09-23-plans-dashboard-design.md`. Адрес собирается
 * ЗДЕСЬ, а не в разметке: клик обязан открывать ровно те строки, которые
 * посчитаны в плитке, и это правило держит `web/checks/planDash.check.ts`.
 */

/** Ступени в порядке жизни партии: план → техкарта → QR-папка → деньги. */
export const STATE_ORDER = ["no_card", "no_qr", "unpaid", "paid"] as const;

export type DashStateKey = (typeof STATE_ORDER)[number];

export type DashSupplier = { id: number | null; name: string; plans: number; qty: number };

export type DashState = {
  state: DashStateKey;
  plans: number;
  qty: number;
  suppliers: DashSupplier[];
};

export type DashCategory = {
  id: number;
  name: string;
  /** Спрашивают ли здесь техкарту — от этого зависит адрес ступеней ниже. */
  card_required: boolean;
  plans: number;
  qty: number;
  states: DashState[];
};

/** Всё, что нужно ссылке от категории: номер и вопрос про техкарту. */
export type HrefCategory = Pick<DashCategory, "id" | "card_required">;

export type PlanDashboard = { categories: DashCategory[] };

/** Слова закупщика, а не ключи ответа: «no_qr» на экране закупки не читается. */
export const STATE_LABELS: Record<DashStateKey, string> = {
  no_card: "нет техкарты",
  no_qr: "нет QR-папки",
  unpaid: "не оплачено",
  paid: "оплачено",
};

/** Чем ступень кончается и чего ждёт — подсказкой у заголовка. */
export const STATE_HINTS: Record<DashStateKey, string> = {
  no_card: "техкарты нет — фабрике нечего шить",
  no_qr: "партия не собрана в QR-папку — платёж привязывать не к чему",
  unpaid: "папка собрана, оплаченного платежа на ней нет",
  paid: "на QR-папке партии есть оплаченный платёж",
};

/**
 * Отбор ступени на «Планах». Значения — `yes` и `no`, как их читает адрес
 * экрана (`PlansPage.tsx::тристорона`): `true`/`false` там молча означают
 * «все», и 23.09.26 клик с ними открывал весь отбор вместо ступени.
 *
 * «Не оплачено» несёт `qrhas=yes`: в воронке это «папка есть, денег нет», а
 * одноимённый отбор «Планов» шире — он включает и партии без папки. Без этой
 * части адреса клик открыл бы больше строк, чем обещало число в плитке.
 */
const STATE_FILTER: Record<DashStateKey, string> = {
  no_card: "card=no",
  no_qr: "qrhas=no",
  unpaid: "qrhas=yes&paid=no",
  paid: "paid=yes",
};

/**
 * Отбор ступени. У категории, где техкарту спрашивают, ступени НИЖЕ неё несут
 * ещё и `card=yes`: воронка строгая, и партия без техкарты стоит в первой
 * ступени, а отбор «нет оплаты» без этой части показал бы её второй раз.
 * Замер 23.09.26 на боевой CRM: 76 партий в отборе против 75 в плитке —
 * лишней была ровно такая партия.
 */
function фильтр(state: DashStateKey, category: HrefCategory): string {
  const карта = category.card_required && state !== "no_card" ? "card=yes&" : "";
  return карта + STATE_FILTER[state];
}

/** Трёхпозиционный отбор адреса «Планов»: пусто — «все». */
export type Tri = "" | "yes" | "no";

/**
 * Отбор дашборда (разметка 28.09.26): окно по дате создания и те же три
 * переключателя, что на «Списке», под теми же ключами адреса — `cfrom`/`cto`,
 * `qrhas`, `paid`, `card`. Плитки посчитаны под ним, и клик обязан унести его
 * с собой, иначе откроет больше строк, чем обещало число.
 */
export type DashFilter = { from: string; to: string; qrhas?: Tri; paid?: Tri; card?: Tri };

/**
 * Хвост адреса: отбор ступени (если она есть), затем отборы экрана, которых
 * ступень не задала, затем окно дат. Совпавший ключ берётся у ступени: у
 * непустой плитки значения всегда равны — воронка строгая, и сервер уже
 * отрезал партии, противоречащие отбору.
 */
function хвост(state: DashStateKey | null, category: HrefCategory, f?: DashFilter): string {
  const части = state ? [фильтр(state, category)] : [];
  const заняты = new Set(части.join("&").split("&").map((kv) => kv.split("=")[0]));
  for (const key of ["qrhas", "paid", "card"] as const) {
    const value = f?.[key];
    if (value && !заняты.has(key)) части.push(`${key}=${value}`);
  }
  if (f?.from) части.push(`cfrom=${f.from}`);
  if (f?.to) части.push(`cto=${f.to}`);
  return части.map((p) => `&${p}`).join("");
}

/** Ступень целиком — та же плитка без поставщика. */
export function dashHref(state: DashStateKey, category: HrefCategory, f?: DashFilter): string {
  return `/plans?status=1&cat=${category.id}${хвост(state, category, f)}`;
}

/**
 * Строки одной фабрики внутри ступени. У партии без поставщика номера нет, и
 * `supplier=` не появляется вовсе: пустой номер отобрал бы не «без
 * поставщика», а вообще ничего. Ступень `null` — режим «без стадий»: все
 * активные строки фабрики в категории под отбором экрана.
 */
export function stateHref(
  state: DashStateKey | null,
  category: HrefCategory,
  supplier: number | null,
  f?: DashFilter,
): string {
  const кто = supplier === null ? "" : `&supplier=${supplier}`;
  return `/plans?status=1&cat=${category.id}${кто}${хвост(state, category, f)}`;
}

/**
 * Строки нескольких фабрик ступени — галочки дашборда и «Открыть» (разметка
 * 28.09.26). Номера уходят через запятую, «Планы» разбирают их в список.
 */
export function suppliersHref(
  state: DashStateKey | null,
  category: HrefCategory,
  suppliers: readonly number[],
  f?: DashFilter,
): string {
  const кто = suppliers.length ? `&supplier=${suppliers.join(",")}` : "";
  return `/plans?status=1&cat=${category.id}${кто}${хвост(state, category, f)}`;
}

/** Одна галочка дашборда: фабрика в ступени категории (`null` — без стадий). */
export type DashPick = { category: HrefCategory; state: DashStateKey | null; supplier: number };

/**
 * Все галочки экрана под ОДНОЙ кнопкой «Открыть» (разметка 28.09.26). «Планы»
 * держат в адресе одну категорию и одну ступень, поэтому галочки разных плиток
 * уходят разными вкладками — по адресу на пару «категория × ступень», в порядке
 * первой отметки.
 */
export function pickedHrefs(picks: readonly DashPick[], f?: DashFilter): string[] {
  const группы = new Map<string, { category: HrefCategory; state: DashStateKey | null; ids: number[] }>();
  for (const pick of picks) {
    const ключ = `${pick.category.id}:${pick.state ?? ""}`;
    const группа = группы.get(ключ);
    if (группа) группа.ids.push(pick.supplier);
    else группы.set(ключ, { category: pick.category, state: pick.state, ids: [pick.supplier] });
  }
  return [...группы.values()].map((g) => suppliersHref(g.state, g.category, g.ids, f));
}

/** Ключ галочки — один на весь экран дашборда. */
export const pickKey = (pick: DashPick): string => `${pick.category.id}:${pick.state ?? ""}:${pick.supplier}`;

/**
 * Галочка плитки (просьба владельца 23.10.26 — «выделить абсолютно все
 * заказы» категории): все строки фабрик, видимые в плитке в текущем режиме.
 * Фабрики без поставщика нет — у неё нет и своей галочки.
 */
export function tilePicks(category: DashCategory, byStates: boolean): DashPick[] {
  const rows: { state: DashStateKey | null; id: number | null }[] = byStates
    ? category.states.flatMap((s) => s.suppliers.map((sup) => ({ state: s.state, id: sup.id })))
    : suppliersOf(category).map((sup) => ({ state: null, id: sup.id }));
  return rows.flatMap((r) => (r.id === null ? [] : [{ category, state: r.state, supplier: r.id }]));
}

/**
 * Режим «без стадий» (разметка 28.09.26): фабрики категории по всем ступеням
 * сразу, крупная первой. Сводится из того же ответа — второй запрос мог бы
 * разойтись с плитками по стадиям.
 */
export function suppliersOf(category: DashCategory): DashSupplier[] {
  const всего = new Map<number | null, DashSupplier>();
  for (const state of category.states)
    for (const s of state.suppliers) {
      const было = всего.get(s.id);
      всего.set(s.id, было ? { ...было, plans: было.plans + s.plans, qty: было.qty + s.qty } : { ...s });
    }
  return [...всего.values()].sort((a, b) => b.plans - a.plans);
}

/**
 * Период «создан» дашборда, запомненный между заходами (разметка 29.09.26
 * 07:30: «запоминай, какой период я выбрал последним, и вставляй его при
 * заходе заново»).
 *
 * ОКНО ЗАПОМИНАЕТСЯ ИМЕНЕМ, А НЕ ДАТАМИ: «30 дней», выбранные вчера, сегодня
 * обязаны значить последние 30 дней, а не вчерашнюю пару дат. Свои даты из
 * календаря запоминаются как есть. Пустая запись — «без периода», и это тоже
 * выбор: сброс не должен воскресать при следующем заходе.
 */
export type CreatedMemory = { window?: string; from?: string; to?: string };

type ОкноДат = { label: string; from: string; to: string };

export function rememberCreated(from: string, to: string, windows: ОкноДат[]): CreatedMemory {
  if (!from && !to) return {};
  const окно = windows.find((w) => w.from === from && w.to === to);
  return окно ? { window: окно.label } : { from, to };
}

/** Что подставить в адрес; `null` — подставлять нечего (пусто или чужая запись). */
export function recallCreated(
  memory: unknown,
  windows: ОкноДат[],
  isDate: (value: string) => boolean,
): { from: string; to: string } | null {
  if (!memory || typeof memory !== "object") return null;
  const m = memory as CreatedMemory;
  if (typeof m.window === "string") {
    const окно = windows.find((w) => w.label === m.window);
    return окно ? { from: окно.from, to: окно.to } : null;
  }
  const from = typeof m.from === "string" && isDate(m.from) ? m.from : "";
  const to = typeof m.to === "string" && isDate(m.to) ? m.to : "";
  return from || to ? { from, to } : null;
}
